import React, { useEffect, useRef, useState } from 'react';
import { Filter as FilterIcon, X, Check } from 'lucide-react';
import SegmentedTabs from 'shared/components/ui/SegmentedTabs';
import { activityToolbarButtonClass } from 'features/activity/styles/dataTableStyles';
import { kindMeta } from 'features/evals3/editor/nodeKinds';

/**
 * The eval page's one filter, and the only one.
 *
 * A Filter button (lucide Filter, like the Traces query builder) with a popup
 * holding every control that narrows the page: time period, version, outcome,
 * and the steps or path a run went through. Whatever is not at its default
 * shows beside the button as a chip with an x. The defaults — the last 30
 * days, every version, every outcome, every run — are what the page opens on,
 * and the page applies the choice across Verdicts, Queue and Analysis alike
 * (a tab shows only the controls that mean something on it).
 */
const kindOf = (instrument) => kindMeta({ type: 'guard', instrument }).label;

export const DEFAULT_DAYS = 30;
export const WINDOWS = [1, 7, 30, 90];
export const windowLabel = (d) => (d === 1 ? 'Last 24 hours' : `Last ${d} days`);
const OUTCOMES = [{ value: 'all', label: 'All' }, { value: 'passed', label: 'Passed' }, { value: 'failed', label: 'Failed' }, { value: 'errored', label: 'Error' }];

const LABEL = 'px-3 pb-1 pt-3 text-[11px] font-medium text-[color:var(--text-muted)]';

function Chip({ children, onRemove, label }) {
  return (
    <span className="inline-flex h-8 items-center gap-1 rounded-lg border border-[color:var(--border-default)] bg-surface pl-2.5 pr-1 text-[12px] text-[color:var(--text-primary)]">
      {children}
      <button type="button" onClick={onRemove} aria-label={`Remove ${label}`} title={`Remove ${label}`}
        className="flex h-6 w-6 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]">
        <X size={13} aria-hidden />
      </button>
    </span>
  );
}

export default function StepFilter({
  steps = [], paths = [], value = [], onChange,
  days = DEFAULT_DAYS, onDays = null,
  versions = [], productionVersion = null, version = '', onVersion = null,
  outcome = 'all', onOutcome = null,
}) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const down = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const key = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', down);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', down); document.removeEventListener('keydown', key); };
  }, [open]);

  const chosen = new Set(value);
  const toggle = (id) => onChange(chosen.has(id) ? value.filter((x) => x !== id) : [...value, id]);
  const same = (a, b) => a.length === b.length && a.every((x) => b.includes(x));
  const nameOf = (id) => steps.find((s) => s.node_id === id)?.name || id;

  const showDays = Boolean(onDays);
  const showVersion = Boolean(onVersion) && versions.length > 0;
  const showOutcome = Boolean(onOutcome);
  const daysOn = showDays && days !== DEFAULT_DAYS;
  const versionOn = showVersion && version !== '';
  const outcomeOn = showOutcome && outcome !== 'all';
  const stepsOn = value.length > 0;
  const count = [daysOn, versionOn, outcomeOn, stepsOn].filter(Boolean).length;
  const reset = () => { onChange([]); if (showDays) onDays(DEFAULT_DAYS); if (showVersion) onVersion(''); if (showOutcome) onOutcome('all'); };

  return (
    <div ref={box} className="relative flex items-center gap-2">
      {daysOn && <Chip label="time period" onRemove={() => onDays(DEFAULT_DAYS)}>{windowLabel(days)}</Chip>}
      {versionOn && <Chip label="version" onRemove={() => onVersion('')}>v{version}</Chip>}
      {outcomeOn && <Chip label="outcome" onRemove={() => onOutcome('all')}>{OUTCOMES.find((o) => o.value === outcome)?.label}</Chip>}
      {stepsOn && <Chip label="steps" onRemove={() => onChange([])}><span title={value.map(nameOf).join(', ')}>{value.length === 1 ? nameOf(value[0]) : `${value.length} steps`}</span></Chip>}
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="dialog" title="Filter" className={activityToolbarButtonClass}>
        <FilterIcon size={13} aria-hidden />
        Filter
        {count > 0 && <span className="text-[11px] tabular-nums text-[color:var(--text-muted)]" data-testid="filter-count">{count}</span>}
      </button>
      {open && (
        <div role="dialog" aria-label="Filter"
          className="absolute right-0 top-full z-50 mt-1 flex max-h-[520px] w-[380px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised,var(--surface-base))] shadow-token-lg">
          <div className="min-h-0 flex-1 overflow-y-auto pb-1">
            {showDays && (
              <>
                <p className={LABEL}>Time period</p>
                <div className="px-3"><SegmentedTabs ariaLabel="Time period" value={String(days)} onChange={(d) => onDays(Number(d))}
                  options={WINDOWS.map((d) => ({ value: String(d), label: d === 1 ? '24h' : `${d}d`, hint: windowLabel(d) }))} /></div>
              </>
            )}
            {showVersion && (
              <>
                <p className={LABEL}>Version</p>
                <div className="px-3"><SegmentedTabs ariaLabel="Version" value={version} onChange={onVersion} className="max-w-full flex-wrap"
                  options={[{ value: '', label: 'All' }, ...versions.map((v) => ({ value: String(v), label: `v${v}`, hint: v === productionVersion ? 'In production' : undefined }))]} /></div>
              </>
            )}
            {showOutcome && (
              <>
                <p className={LABEL}>Outcome</p>
                <div className="px-3"><SegmentedTabs ariaLabel="Outcome" value={outcome} onChange={onOutcome} options={OUTCOMES} /></div>
              </>
            )}
            <p className={LABEL}>Steps</p>
            {steps.length === 0 && <p className="px-3 py-1 text-[12px] text-[color:var(--text-muted)]">None yet</p>}
            {steps.map((st) => (
              <label key={st.node_id} className="flex cursor-pointer items-center gap-2 px-3 py-1 text-[12px] hover:bg-surface-hover">
                <input type="checkbox" checked={chosen.has(st.node_id)} onChange={() => toggle(st.node_id)} className="h-3.5 w-3.5 accent-[color:var(--trodo-primary)]" />
                <span className="min-w-0 flex-1 truncate text-[color:var(--text-primary)]" title={st.name}>{st.name}</span>
                <span className="shrink-0 text-[11px] text-[color:var(--text-muted)]">{kindOf(st.instrument)}</span>
              </label>
            ))}
            {paths.length > 0 && (
              <>
                <p className={LABEL}>Paths</p>
                {paths.map((p) => {
                  const ids = p.path.slice(0, -1);
                  const on = ids.length > 0 && same(ids, value);
                  return (
                    <button key={p.path.join('>')} type="button" onClick={() => onChange(on ? [] : ids)} aria-pressed={on}
                      className="flex w-full items-start gap-2 px-3 py-1 text-left text-[12px] hover:bg-surface-hover" title={p.names.join(' → ')}>
                      <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center">{on && <Check className="h-3 w-3 text-[color:var(--trodo-primary)]" aria-hidden />}</span>
                      <span className="line-clamp-2 min-w-0 flex-1 text-[color:var(--text-primary)]">
                        {p.names.slice(0, -1).join(' → ')} → <span className="font-medium">{p.names[p.names.length - 1]}</span>
                      </span>
                      <span className="mt-0.5 shrink-0 text-[11px] tabular-nums text-[color:var(--text-muted)]">{p.runs}</span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
          <div className="flex items-center justify-between border-t border-[color:var(--border-subtle)] px-3 py-1.5">
            <button type="button" onClick={reset} disabled={count === 0}
              className="text-[12px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] disabled:opacity-40">Reset</button>
            <button type="button" onClick={() => setOpen(false)}
              className="rounded px-2 py-0.5 text-[12px] font-medium text-[color:var(--text-primary)] hover:bg-surface-hover">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
